// The signed-in user's menu — who you are, Profile, Sign out (with a confirm),
// opened from the top bar's account button. An earlier copy passed PaperProps,
// which MUI v9 ignores, so its offset, width and shadow never applied;
// slotProps.paper is what v9 reads.
import React, { useState } from 'react';
import { Box, Divider, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { Logout, Person } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useAuth } from '@app/providers/AuthProvider';
import ConfirmDialog from '@shared/components/ui/ConfirmDialog';

export const fullName = (user) => `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim() || 'User';

/** The role shown under a user's name — their first role, as the profile page has always shown it. */
export const primaryRoleName = (user) => user?.roles?.[0]?.name ?? null;

export default function AccountMenu({
  anchorEl,
  onClose,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}) {
  const { user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const openProfile = () => { onClose(); navigate('/profile'); };
  const askToSignOut = () => { onClose(); setConfirming(true); };
  const signOutNow = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
      setConfirming(false);
    }
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{ paper: { elevation: 4, sx: { minWidth: 220, mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{fullName(user)}</Typography>
          {user?.email && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{user.email}</Typography>}
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={openProfile}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={askToSignOut} disabled={loading}>
          <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={confirming}
        onClose={() => { if (!signingOut) setConfirming(false); }}
        onConfirm={signOutNow}
        title="Sign out?"
        message="You'll need to log in again to access your account."
        confirmText="Sign out"
        cancelText="Stay signed in"
        busy={signingOut}
      />
    </>
  );
}
